import { useContext, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { BsSave } from "react-icons/bs";
import { useParams } from "react-router";
import { useNavigate } from "react-router";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import Spinner from "../../../shared/components/spinner/spinner";
import ToolTip from "../../../shared/components/tooltip/tooltip";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { failedToast, getAllData, getData, postSingleNonTable } from "../../../shared/utils/api";
import WidgetComponent, { IWidgetLayout } from "../../components/widgets-layout/widget-component/widget-component";
import { IWidgetModal } from "../../components/widgets-layout/widget-utils";
import "./screen.scss";
import { createUUID, validateLoadingStatusNew } from "../../../shared/utils/utils";
import { useHttpGet } from "../../../shared/hooks/use-httpNonTable";
import { usePrompt } from "../../../shared/utils/use-prompt";
import Page from "../../components/page/page";

const Screen = () => {
  const params = useParams();
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  const [layout, setLayout] = useState<IWidgetLayout>({layout: [], layoutItems: []});

  const { sendRequest: postScreen, status: postScreenStatus, error: postScreenError, data: postScreenData } = useHttp(postSingleNonTable, false, authCtx.token, "screen",  "manager");
  const { sendRequest: getScreen, status: getScreenStatus, error: getScreenError, data: getScreenData } = useHttp(getAllData, false, authCtx.token, "screen", "manager");
  const { sendRequest: getManagerConfig, status: getManagerConfigStatus, data: getManagerConfigData, error: getManagerConfigError } = useHttpGet(getData, false, authCtx.token, "managerConfig", "manager");

  const [title, setTitle] = useState<string>(getScreenData?.name || "");
  const [alertcase, setAlertcase] = useState<string>("");
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isFirst, setIsFirst] = useState<boolean>(true);
  const { flag, setFlag, next } = usePrompt(isDirty);    
  const pageIsNew = !params.id;
  const [hasTitle, setHasTitle] = useState<boolean>(false);

  useEffect(() => {
    getManagerConfig("configuration");
    if (!pageIsNew) {
      getScreen(`screen/${params.id}`);
    } else {
      setIsDirty(true);
    }
  }, []);

  useEffect(() => {
    if (isFirst) {
      setIsFirst(false);
    } else {
      if(!pageIsNew && getScreenStatus === "completed" && hasTitle) {
        if(title !== getScreenData?.name) {
          setIsDirty(true);
        } else {
          setIsDirty(false);
        }
      }
    }
  }, [title, getScreenData]);

  useEffect(() => {
    if (getScreenStatus === "completed" && getScreenData && !getScreenError) {
      setLayout(getScreenData.layout);
      setTitle(getScreenData.name);
      setHasTitle(true);
    }
  }, [getScreenStatus, getScreenData, getScreenError]);

  useEffect(() => {
    if (postScreenStatus === "completed" && !postScreenError && postScreenData) {
      navigate("/manager/screens");
    }
  }, [postScreenStatus, postScreenError, postScreenData]);

  useEffect(() => {
    if (getManagerConfigStatus === "completed" && getManagerConfigData && !getManagerConfigError) {
      setAlertcase(getManagerConfigData.manager_alertcase_id);
    }
  }, [getManagerConfigStatus, getManagerConfigData, getManagerConfigError]);
  
  const handleScreenSave = () => {
    setIsDirty(false);
    const screenId = pageIsNew ? createUUID() : params.id;
    if (title === "") {
      failedToast("Screen name is required");
      return;
    }
    postScreen(`screen/${screenId}`, {
      id: screenId,
      name: title,
      layout: layout,
      alertcase_id: alertcase
    });
  };

  const widgets: IWidgetModal[] = [
    {
      type: "text"
    },
    {
      type: "datamodelField",
      datamodelType: "alertcase",
      datamodelId: alertcase
    },
    {
      type: "datamodelTable"
    },
    {
      type: "datamodelRelatedEntity"
    },
    {
      type: "datamodelRelatedEvent"
    },
    {
      type: "datamodelRelatedAlertedEvent"
    }
  ];

  const handleLayoutChange = (layout: IWidgetLayout) => {
    setLayout(layout);
    setIsDirty(true);
  };

  return <Page title={{
    text: title,
    onTitleEdited: (title: string) => {
      setTitle(title);
    },
    placeholder: "Screen name"
  }}
  breadCrumbItems={[
    {
      name: "Screens",
      url: "/manager/screens"
    },
    {
      name: "Screen"
    }
  ]}
  >
    <div className="screen">
      <div className="screen-saveButton">
        <ToolTip content={"Save"} direction="top" delay={350}  >
          <Button disabled={postScreenStatus === "pending"} name="save" className="screen-save" onClick={handleScreenSave}>
            <BsSave />
          </Button>
        </ToolTip>
      </div>
      <div>
        { validateLoadingStatusNew([getManagerConfigStatus, getScreenStatus, postScreenStatus]) && <Spinner /> }
        <WidgetComponent 
          handleLayoutChange={handleLayoutChange}
          widgets={widgets}
          layout={layout} />
      </div>

      {flag && <ConfirmationPopup 
        show={true}
        handleSaveClick={handleScreenSave}
        handlePopupClosed={() => {
          setFlag(false);
        }}
        handleContinue={() => { next(); setFlag(false); }} />}

    </div>
  </Page>;
};

export default Screen;